import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Lock } from 'lucide-react';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Cache de instâncias loadStripe por publishable key (a chave vem do backend/segredos)
const _stripeCache = {};
const getStripe = (pk) => {
  if (!pk) return null;
  if (!_stripeCache[pk]) _stripeCache[pk] = loadStripe(pk);
  return _stripeCache[pk];
};

const CheckoutForm = ({ amount, baseAmount, onlineFee, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setErrorMsg('');
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/payment-return` },
      redirect: 'if_required',
    });
    if (error) {
      // Erro de validação/cartão recusado/Klarna indisponível
      setErrorMsg(error.message || 'O pagamento falhou. Por favor tente novamente.');
      setLoading(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent);
    } else {
      // Métodos com redirecionamento (ex: Klarna) — o browser será redirecionado
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handlePay} data-testid="stripe-payment-form" className="space-y-4">
      {onlineFee > 0 && (
        <div className="rounded-xl bg-[#FAF8F4] border border-[#E6E2DA] p-3 space-y-1 text-sm" data-testid="modal-fee-breakdown">
          <div className="flex justify-between text-[#635E54]"><span>Serviços</span><span>€{Number(baseAmount).toFixed(2)}</span></div>
          <div className="flex justify-between text-[#635E54]"><span>Taxa de serviço *</span><span>€{Number(onlineFee).toFixed(2)}</span></div>
          <div className="flex justify-between font-semibold text-[#1A1815] pt-1 border-t border-[#E6E2DA]"><span>Total</span><span>€{Number(amount).toFixed(2)}</span></div>
          <p className="text-[10px] text-[#A8A29A] pt-1">* Valor ajustado devido à taxa de serviço aplicada aos pagamentos online (Stripe).</p>
        </div>
      )}
      <PaymentElement options={{ layout: 'tabs', defaultValues: { billingDetails: { address: { country: 'PT' } } } }} />
      {errorMsg && (
        <p data-testid="payment-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          {errorMsg}
        </p>
      )}
      <Button
        type="submit"
        data-testid="pay-now-btn"
        disabled={!stripe || loading}
        className="w-full rounded-full bg-[#1A1815] text-white hover:bg-[#BFA05F] py-6 text-base transition-colors"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A processar...</>
        ) : (
          <><Lock className="w-4 h-4 mr-2" /> Pagar €{Number(amount).toFixed(2)}</>
        )}
      </Button>
      <p className="text-xs text-center text-[#A8A29A]">Pagamento seguro processado por Stripe</p>
    </form>
  );
};

export const StripePaymentModal = ({ open, onClose, bookingsPayload, onSuccess }) => {
  const [clientSecret, setClientSecret] = useState('');
  const [amount, setAmount] = useState(0);
  const [baseAmount, setBaseAmount] = useState(0);
  const [onlineFee, setOnlineFee] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    let active = true;
    if (open && bookingsPayload && bookingsPayload.length > 0 && !clientSecret) {
      setLoading(true);
      api.post('/payments/create-intent', { bookings: bookingsPayload })
        .then((res) => {
          if (!active) return;
          const pk = res.data.publishable_key || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
          if (!pk) {
            toast.error('Pagamento online indisponível: chave Stripe em falta. Contacte o salão.');
            onClose();
            return;
          }
          setClientSecret(res.data.client_secret);
          setAmount(res.data.amount);
          setBaseAmount(res.data.base_amount ?? res.data.amount);
          setOnlineFee(res.data.online_fee ?? 0);
          setStripePromise(getStripe(pk));
        })
        .catch((err) => {
          toast.error(err.response?.data?.detail || 'Erro ao iniciar o pagamento');
          onClose();
        })
        .finally(() => active && setLoading(false));
    }
    if (!open) {
      setClientSecret('');
      setAmount(0);
      setBaseAmount(0);
      setOnlineFee(0);
      setStripePromise(null);
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bookingsPayload]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="stripe-payment-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1A1815]">Pagamento</DialogTitle>
        </DialogHeader>
        {loading || !clientSecret || !stripePromise ? (
          <div className="py-12 flex flex-col items-center gap-3">
            <Loader2 className="w-7 h-7 animate-spin text-[#BFA05F]" />
            <p className="text-sm text-[#635E54]">A preparar o pagamento seguro...</p>
          </div>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: 'stripe', variables: { colorPrimary: '#BFA05F', borderRadius: '10px' } },
            }}
          >
            <CheckoutForm amount={amount} baseAmount={baseAmount} onlineFee={onlineFee} onSuccess={onSuccess} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StripePaymentModal;
